import { Component, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { merge } from 'rxjs';
import { map, scan, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { CampaignDialType, ScheduledCampaignDto } from '../models';
import { SchedCheckService, WorkService } from '../services';
import { SubSink } from 'subsink';
import { DiallerService, SIPService } from '@app/dialler/services';
import { Unity4Claim } from '@adreno-common';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-scheduled-campaigns',
  templateUrl: './scheduled-campaigns.component.html',
  styles: [
    `
      .campaign-item {
        max-width: 200px;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        cursor: default;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScheduledCampaignsComponent implements OnDestroy {
  private subs = new SubSink();

  Unity4Claim = Unity4Claim;

  viewModel$ = merge(
    this.workSvc.scheduledCampaigns$.pipe(map((campaigns) => ({ campaigns }))),
    this.workSvc.campaignId$.pipe(map((campaignId) => ({ campaignId }))),
    this.workSvc.hasIBScheduled$.pipe(map((ib) => ({ ib }))),
    this.workSvc.omniChannel$.pipe(map((omniChannel) => ({ omniChannel })))
  ).pipe(scan((acc, value) => ({ ...acc, ...value }), {} as ViewModel));

  constructor(
    private workSvc: WorkService,
    private router: Router,
    private sipSvc: SIPService,
    private toastrSvc: ToastrService,
    private diallerSvc: DiallerService,
    private schedChkSvc: SchedCheckService
  ) {}

  launch(e: Event, campaignId: number, dialType: CampaignDialType) {
    this.schedChkSvc
      .lateCheck(campaignId)
      .pipe(
        switchMap(() => this.workSvc.isUserScheduled(campaignId)),
        withLatestFrom(this.workSvc.obLaunched$),
        withLatestFrom(this.workSvc.campaignScriptOpen$)
      )
      .subscribe(([[isSched, launched], isScriptOpen]) => {
        if (launched === false || this.workSvc.inUnavailableState === true) {
          if (!isSched) {
            this.toastrSvc.error(
              'You cannot enter the queue outside scheduled hours'
            );
            this.workSvc.exit();
          } else {
            if (!isScriptOpen) {
              e.stopPropagation();
              this.workSvc.launchCampaign(campaignId, dialType);
            }
          }
        }
      });
  }

  launchib(e: Event, campaignId: number, dialType: CampaignDialType) {
    this.schedChkSvc
      .lateCheck(campaignId)
      .pipe(
        switchMap(() => this.workSvc.campaignScriptOpen$),
        take(1)
      )
      .subscribe((scriptOpen) => {
        if (!scriptOpen) {
          e.stopPropagation();
          this.diallerSvc.launchCampaignIB(campaignId, dialType);
        }
      });
  }

  launchuat(
    e: Event,
    campaignId: number,
    dialType: CampaignDialType,
    actionType: string
  ) {
    this.schedChkSvc
      .lateCheck(campaignId)
      .pipe(
        switchMap(() => this.workSvc.isUserScheduled(campaignId)),
        withLatestFrom(this.workSvc.campaignScriptOpen$),
        take(1)
      )
      .subscribe(([isSched, scriptOpen]) => {
        if (!isSched) {
          this.toastrSvc.error(
            'You cannot enter the queue outside scheduled hours'
          );
          this.workSvc.exit();
        } else {
          if (!scriptOpen) {
            e.stopPropagation();
            this.workSvc.launchCampaignUAT(campaignId, dialType, actionType);
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  omniChannel() {
    this.workSvc.omniChannel = true;
    this.router.navigate(['/work', 'omni-channel']);
  }

  select(campaignId: number) {
    this.workSvc.obLaunched$
      .pipe(take(1), withLatestFrom(this.workSvc.campaignScriptOpen$))
      .subscribe(([oblaunched, scriptOpen]) => {
        if (!oblaunched && !scriptOpen) this.workSvc.selectCampaign(campaignId);
      });
  }
}

interface ViewModel {
  campaignId?: number;
  campaigns?: ScheduledCampaignDto[];
  ib?: boolean;
  omniChannel?: boolean;
}
